import { message } from 'antd';
import { MutableRefObject } from 'react';

import { UseTranslateType } from '@/hooks/useTranslate';
import { stripeOrderDetail, stripeOrderPay } from '@/services/order';
import { AIFunction } from '@/utils/const';
import { fc_purchase_alert_expose } from '@/utils/eventlog/eventNewConfig';

import { getFeatureSource } from '../utils';
import { OrderInfo } from './usePayment';

interface StripePayProps {
  feature: fc_purchase_alert_expose['from'];
  commodityCode: string;
  type: string;
  pollPayKey: string;
  timer: MutableRefObject<NodeJS.Timeout | null>;
  t: UseTranslateType;
  callback: { success: (orderId: string) => void; error: (msg: string) => void };
}

interface StripePollingProps {
  orderInfo: OrderInfo;
  t: UseTranslateType | undefined;
  pollPayKey: string;
  timer: MutableRefObject<NodeJS.Timeout | null>;
  callback: { success: () => void; error: (msg: string) => void };
}

export const stripePay = async ({
  feature,
  commodityCode,
  type,
  callback,
}: Omit<StripePayProps, 'timer' | 'pollPayKey' | 't'>) => {
  if (type === 'combo') {
    const detail = await stripeOrderDetail();
    if (
      !detail.data.status ||
      detail.data.status === 'UNKNOWN' ||
      detail.data.status === 'FAILED' ||
      detail.data.status === 'CANCEL'
    ) {
      await handleStripeOrder({
        feature,
        commodityCode: String(commodityCode),
        callback,
      });
    } else {
      window.open(detail.data.redirectUrl, '_blank', 'noopener=yes,noreferrer=yes');
    }
  } else {
    await handleStripeOrder({
      feature,
      commodityCode: String(commodityCode),
      callback,
    });
  }
};

export const handleStripeOrder = async ({
  feature = AIFunction.Other,
  commodityCode,
  callback,
}: Omit<StripePayProps, 'timer' | 'type' | 'pollPayKey' | 't'>) => {
  let content = {
    channel: 'stripe',
    commodityCode,
    redirectUrl: window.location.href,
    extend: JSON.stringify({
      source: getFeatureSource(feature),
    }),
    supportDistribution: false,
  };

  try {
    const res = await stripeOrderPay(content);
    if (res.code === 200 && res.success) {
      callback.success(res.data.subscribeId);
      window.open(res.data.redirectUrl, '_blank', 'noopener=yes,noreferrer=yes');
    } else {
      callback.error(res.msg || '/rest/dlt/commerce/subscribe callback error');
    }
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const stripePolling = ({
  orderInfo,
  t,
  pollPayKey,
  timer,
  callback,
}: StripePollingProps) => {
  return new Promise<void>((resolve, reject) => {
    const poll = async (attempts: number) => {
      timer.current && clearTimeout(timer.current);
      try {
        if (attempts > 60 * 2) {
          timer.current && clearTimeout(timer.current);
          callback.error('timeout');
          return resolve();
        }
        const params = { orderId: orderInfo.orderId };
        const data = await stripeOrderDetail(params);
        if (data.success && data.data.status === 'SUCCESS') {
          callback.success();
          timer.current && clearTimeout(timer.current);
          return resolve();
        } else {
          timer.current = setTimeout(() => poll(attempts + 1), 2000);
          if (attempts === 60) {
            message.open({
              key: pollPayKey,
              type: 'warning',
              content: t && t('merror.sub.noresult.toast'),
              duration: 3,
            });
          }
        }
      } catch (error) {
        timer.current && clearTimeout(timer.current);
        callback.error((error as any).message || 'loop fail');
        return reject(error);
      }
    };
    if (!orderInfo.orderId) {
      return;
    }
    poll(0);
  });
};
